import './App.css';
import { useState, useEffect, useRef } from 'react';
import SvgLogo from './assets/icons/Logo';
import { CreditAlertHero, GreenBook, MdiFacebook, MdiInstagram, MdiLinkedin, MdiTwitter, PaperRed, Percent, Sparkles, Svg1, Svg2, Svg3, SvgBamBam, SvgYoutube, WhiteBook, WhiteHeadphone, WhitePaper, YellowHeadphone } from './assets/icons';
import SvgPoint from './assets/icons/Point';
import SvgStar from './assets/icons/Star';
import SvgArrow from './assets/icons/Arrow';
import SvgPercent from './assets/icons/Percent';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// YouTube IFrame API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: (() => void) | null;
  }
}

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

interface YouTubePlayer {
  playVideo: () => void;
  pauseVideo: () => void;
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const sectionRef = useRef<HTMLElement>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const playerDivRef = useRef<HTMLDivElement>(null);
  const [isPlayerReady, setIsPlayerReady] = useState<boolean>(false);

  const sections = [
    { id: 'why', name: 'Why this book' },
    { id: 'apply', name: 'Apply for business grant' },
    { id: 'about', name: 'About author' },
  ];

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start' 
      });
    }
    setIsMenuOpen(false); // Close mobile menu after clicking
  };

  // GSAP animation for sections
  const whySectionRef = useRef(null);
  const whyContentRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Select all elements to animate
      const elements = gsap.utils.toArray('.animate-item');
      
      // Set initial state
      gsap.set(elements, { opacity: 0, y: 20 });
      
      // Create animation timeline
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: whySectionRef.current,
          start: "top 70%",
          toggleActions: "play none none none",
        }
      });
      
      // Animate each element sequentially
      tl.to(elements, {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.3
      });
      
    }, whyContentRef); // scope selector to contentRef

    return () => ctx.revert(); // cleanup
  }, []);

  const svgRef = useRef(null);

  useEffect(() => {
    // Create the animation when component mounts
    gsap.to(svgRef.current, {
      y: -20, // move up 10 pixels
      duration: 2, // duration of one way animation
      repeat: -1, // infinite repeats
      yoyo: true, // go back and forth
      ease: "sine.inOut" // smooth easing
    });
  }, []);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 100; // Offset for header height
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load YouTube IFrame API
  useEffect(() => {
    // Check if YouTube API is already loaded
    if (window.YT && window.YT.Player) {
      initializePlayer();
      return;
    }

    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag && firstScriptTag.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // YouTube API ready callback
    window.onYouTubeIframeAPIReady = initializePlayer;

    return () => {
      // Cleanup
      if (window.onYouTubeIframeAPIReady === initializePlayer) {
        window.onYouTubeIframeAPIReady = null;
      }
    };
  }, []);

  const initializePlayer = (): void => {
    if (playerDivRef.current) {
      new window.YT.Player('youtube-player', {
        height: '315',
        width: '560',
        videoId: 'VUtc1kUsyRk', // Replace with your actual YouTube video ID
        playerVars: {
          autoplay: 0,
          mute: 0, // Required for autoplay to work in most browsers
          controls: 1,
          rel: 0,
          modestbranding: 1,
          playsinline: 1
        },
        events: {
          onReady: (event: any) => {
            playerRef.current = event.target;
            setIsPlayerReady(true);
          }
        }
      });
    }
  };

  // Intersection Observer for auto-play/pause
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current) return;

    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Section is in view - play video
            try {
              playerRef.current?.playVideo();
            } catch (error) {
              console.log('Could not play video:', error);
            }
          } else {
            // Section is out of view - pause video
            try {
              playerRef.current?.pauseVideo();
            } catch (error) {
              console.log('Could not pause video:', error);
            }
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of the section is visible
        rootMargin: '0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [isPlayerReady]);

  useEffect(() => {
    // Load the YouForm embed script dynamically
    const script = document.createElement('script');
    script.src = 'https://app.youform.com/embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Clean up by removing the script when component unmounts
      document.body.removeChild(script);
    };
  }, []);
  const width = '100%';

  const openSelar = () => {
    // open selar in a new tab
    window.open('https://selar.com/9x6716212a', '_blank');
  }

  const openEbookSelar = () => {
    // open selar in a new tab
    window.open('https://selar.com/i8tz72s84a', '_blank');
  }

  const openAudioSelar = () => {
    // open selar in a new tab
    window.open('https://selar.com/450g407015', '_blank');
  }

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[80px]">
            <div className='flex space-x-4'>
              {/* Logo */}
              <div className="flex-shrink-0">
                <button 
                  onClick={() => scrollToSection('home')}
                  className="flex items-center hover:opacity-80 transition-opacity"
                >
                  <SvgLogo width="171px" height="45px" />
                </button>
              </div>

              {/* Desktop Navigation */}
              <div className="ml-10 hidden md:flex items-center space-x-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-200 
                        text-gray-900 hover:text-green-600`}
                  >
                    {section.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop CTA Button */}
            <div className="hidden md:block">
              <button 
                onClick={() => scrollToSection('choose')}
                className="bg-[#007A3D] hover:bg-green-700 text-white px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200"
              >
                Get your copy  
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-500 hover:text-gray-900 focus:outline-none focus:text-gray-900 p-2"
              >
                {isMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-3 py-2 text-base font-medium border-l-4 transition-colors duration-200 ${
                      activeSection === section.id
                        ? 'text-green-600 border-green-600 bg-green-50'
                        : 'text-gray-500 border-transparent hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {section.name}
                  </button>
                ))}
                <div className="px-3 py-2">
                  <button 
                    onClick={() => scrollToSection('choose')}
                    className="w-full bg-[#007A3D] hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Get your copy  
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content: Home Section */}
      <section id="home" className="lg:min-h-[calc(800px-80px)] flex items-center justify-center bg-gradient-to-b from-[#F1F6F4] to-white">
        <div className='container mx-auto pt-[20px] lg:pt-0 px-4 lg:px-8 flex flex-col md:flex-row lg:justify-between'>
          <div className='w-full md:w-1/2 max-w-[460px] flex flex-col justify-center'>
            <h1 className="text-4xl md:text-5xl leading-[1.1em] font-bold text-gray-900 mb-6">Your customers have money.</h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            They are spending it right now. Just not with you. Credit Alert breaks down exactly how to fix that, and how to sell to Nigerians
            </p>
            <div className='w-full flex flex-col items-center lg:flex-row gap-4'>
              <button 
                onClick={() => scrollToSection('choose')}
                className="bg-[#007A3D] border border-[#007A3D] text-white w-fit p-3 rounded-lg text-lg font-medium flex items-center hover:-translate-y-1 transition-all duration-200"
              >
                Get your copy  
              </button>
              <button 
                onClick={() => scrollToSection('apply')}
                className="bg-white border border-[#007A3D] text-[#007A3D] w-fit p-3 rounded-lg text-lg font-medium hover:-translate-y-1 transition-all duration-200"
              >
                Apply for Business Grant
              </button>
            </div>
          </div>
          <div className='w-full md:w-1/2 flex justify-end'>
            <div ref={svgRef} className='w-full h-full max-w-[590px]'>
              <CreditAlertHero width="100%" height="582px" />
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="why" ref={whySectionRef} className="lg:min-h-[750px] flex flex-col-reverse lg:flex-row items-center justify-center bg-white">
        <div className="w-full lg:w-1/2 h-full flex items-center justify-center pt-[30px] lg:p-0">
          <img 
            src="https://res.cloudinary.com/dizhwjlge/image/upload/v1752956411/Another-Lolu-Copy_jntebi.jpg"
            alt="Book Cover"
            className="max-w-full h-auto object-contain"
          />
        </div>
        <div ref={whyContentRef}  className='w-full lg:w-1/2 flex flex-col items-center px-4 lg:px-0 justify-center gap-20'>
          {/* Testimonial */}
          <div className='mt-8 text-left w-full max-w-[500px]'>
            <div className='bg-gray-100 p-6 rounded-lg shadow-sm'>
              <p className='text-gray-700 italic'>"This book is exactly that; a roadmap for anyone trying to sell to, win with, and build for the
Nigerian customer. It’s honest, funny, and grounded in lived experience. If
you’re looking for theories and jargon, this book isn’t for you. But if you want to actually succeed
in this market, pay close attention."</p>
            </div>
            <div className='flex items-center mt-4'>
              <img 
                src="https://res.cloudinary.com/dizhwjlge/image/upload/v1752956523/63495ef0a9c0d_zydksu.jpg" 
                alt="Akinlabi thumbnail" 
                className='w-12 h-12 rounded-full object-cover'
              />
              <div className='ml-4'>
                <p className='font-semibold text-gray-900'>Akin Alabi</p>
                <p className='font-semibold text-gray-900 text-sm'>Best-Selling Author - Small Business Big Money</p>
              </div>
            </div>
          </div>
          <div className='w-full max-w-[500px] flex flex-col justify-center gap-6'>
            {/* <div className='w-fit rounded-full px-3 py-2 bg-[#109A5514] text-[#109A55] flex justify-center items-center space-x-2'>
              <SvgPercent width="24px" height="24px" />
              <p className="text-sm md:text-base text-center font-semibold">
                Pre-Order Book for <span className='line-through'>₦7500</span> ₦5000 
              </p>
            </div> */}
            <div className='w-full flex flex-col space-y-3'>
              <h1 className="text-[30px] md:text-[48px] leading-[1em] font-bold text-gray-900 mb-6">Why business owners are getting this book</h1>
              <div className='w-full flex flex-col space-y-2'>
                <div className='flex items-start space-x-2'>
                  <span className='w-[30px] h-[30px] flex items-center justify-center'>
                    <SvgPoint width="22px" height="22px" />
                  </span>
                  <p className="text-md md:text-base text-gray-600 flex-1">
                  Understand how Nigerians think about money and trust.
                  </p>
                </div>
                <div className='flex items-start space-x-2'>
                  <span className='w-[30px] h-[30px] flex items-center justify-center'>
                    <SvgPoint width="22px" height="22px" />
                  </span>
                  <p className="text-md md:text-base text-gray-600 flex-1">
                  Learn how to sell without sounding desperate.
                  </p>
                </div>
                <div className='flex items-start space-x-2'>
                  <span className='w-[30px] h-[30px] flex items-center justify-center'>
                    <SvgPoint width="22px" height="22px" />
                  </span>
                  <p className="text-md md:text-base text-gray-600 flex-1">
                  Use strategies that make people come back and refer you.
                  </p>
                </div>
                <div className='flex items-start space-x-2'>
                  <span className='w-[30px] h-[30px] flex items-center justify-center'>
                    <SvgPoint width="22px" height="22px" />
                  </span>
                  <p className="text-md md:text-base text-gray-600 flex-1">
                  Real case studies. Real results. Real people.
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => scrollToSection('choose')}
              className="bg-[#007A3D] text-white w-fit p-3 rounded-lg text-lg font-medium hover:-translate-y-1 transition-all duration-200"
            >
              Get your copy
            </button>
          </div>
        </div>
      </section>

      <section className='w-full flex flex-col space-y-3 justify-center items-center py-10'>
        <h2 className="text-[30px] md:text-[48px] leading-[1em] font-bold text-gray-900 mb-6 text-center">Read the Intro Chapter for Free</h2>
        <div className="book-page">
          <div className="header-title">Introduction</div>
          
          <h1 className="main-title">
            If You Are Selling to Nigerians, I Really Feel For You
          </h1>
          
          <div className="quote-block">
              <p className="quote-text">
                  promise to tell the truth and nothing but the truth.
              </p>
          </div>
          
          <p className="body-text">
              Nigeria is not anybody's mate.
          </p>
          
          <p className="body-text">
              And if you're one of the brave ones running a business in Nigeria, you already know that this place will test your patience, your product, your pitch, and your destiny. Nigerians have seen it all. From <span className="quote-inline">"Buy one, get one free!"</span> To <span className="quote-inline">"Direct from Dubai."</span> From the polished online vendor with the perfect aesthetics to the hawker on the street who can run faster than Usain Bolt. They've seen it all.
          </p>
          
          <p className="body-text">
              The real, the fake, and even the straight-up spiritual, they've seen that one too. Because of that, Nigerians are sharp. They're sceptical, and they're not easily moved.
          </p>
          
          <p className="body-text">
              You can't just come with <span className="emphasis">innit innit IJGB</span> accent and a Canva graphic and expect Nigerians to <span className="emphasis">rush your market</span>. It won't work. This is not a copy-and-paste market. This is a "show me, prove it, let me ask my friend if they know you" market. That's exactly why if you figure out how to sell here, you've unlocked a superpower.
          </p>

          <button
            onClick={() => scrollToSection('choose')}
            className="bg-[#007A3D] text-white w-fit p-3 rounded-lg text-lg font-medium hover:-translate-y-1 transition-all duration-200"
          >
            Continue reading
          </button>
        </div>
      </section>

      {/* Businesses Mentioned Section */}
      <section id="businesses" className="py-20 bg-white overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-[30px] md:text-[48px] leading-[1em] font-bold text-gray-900 mb-12 text-center">
            Learn from business examples like
          </h2>
          <div className="relative flex">
            <div className="marquee-container">
              {[...Array(2)].map((_, i) => (
                [
                  { name: 'Paystack', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Paystack_Logo.png/1200px-Paystack_Logo.png' },
                  { name: 'House of Tara', logo: 'https://houseoftara.com/wp-content/uploads/2019/03/cropped-logo.png' },
                  { name: 'Quacktails', logo: 'https://pbs.twimg.com/profile_images/1532763536581185537/BFjGuEza_400x400.jpg' },
                  { name: 'Piggyvest', logo: 'https://storage.googleapis.com/piggyvestwebsite/piggywebsite2020/logo_08e6965627/logo_08e6965627.svg' },
                  { name: 'DyeLab', logo: 'https://dyelab.ng/cdn/shop/t/6/assets/logo.png?v=15591019475372549261741777176' },
                  { name: 'Dang Lifestyle', logo: 'https://danglifestyle.co/wp-content/uploads/2024/04/dang-logo.svg' },
                  { name: "Dangote", logo: "https://e7.pngegg.com/pngimages/797/923/png-clipart-logo-dangote-group-lagos-brand-dangote-cement-business-people-logo.png" },
                  { name: 'The Nail Boutique', logo: 'https://thenailboutique.ng/wp-content/uploads/2022/05/logo-white.png' },
                  { name: 'TDA Couture', logo: 'https://tdagroup.africa/wp-content/uploads/2019/12/tda-couture-logo.png' },
                ].map((business) => (
                  <div key={`${business.name}-${i}`} className="marquee-item flex-shrink-0 mx-8">
                    <img src={business.logo} alt={business.name} className="h-10" />
                  </div>
                ))
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* grant */}
      <section id="apply" className="lg:min-h-[750px] relative flex-col items-start justify-center py-[100px] bg-white">
        <div className='container mx-auto pt-[20px] lg:pt-0 px-4 lg:px-8 flex flex-col md:flex-row lg:justify-between gap-6'>
          <div className='w-full flex flex-col justify-center'>
            <div className='w-full flex flex-col'>
              <h2 className="text-4xl md:text-5xl leading-[1.2em] font-bold text-gray-900 mb-6">Apply for the Credit Alert Business Grant worth  ₦10,000,000 </h2>
              
              <p className="text-base md:text-lg text-gray-600 mb-4">
              The Credit Alert Business Grant is a support package designed to help Nigerian entrepreneurs scale faster and smarter. It’s exclusively available to readers of the Credit Alert book.
              </p>
              <p className="text-base md:text-lg text-gray-600 mb-6 font-medium">
                Here’s what’s included:
              </p>
              <div className="space-y-5">
                <div className='space-y-1'>
                  <h3 className="text-xl font-semibold text-gray-900">Cash Grant</h3>
                  <p className="text-base text-gray-600">Each winner will receive a cash grant of N2,000,000.</p>
                </div>
                <div className='space-y-1'>
                  <h3 className="text-xl font-semibold text-gray-900">Marketing Support</h3>
                  <p className="text-base text-gray-600">Each winner will get a marketing package covering social media campaigns and offline visibility boosts.</p>
                </div>
                <div className='space-y-1'>
                  <h3 className="text-xl font-semibold text-gray-900">Business Coaching</h3>
                  <p className="text-base text-gray-600">Each winner will have access to one-on-one mentorship and strategy sessions with top industry leaders for 1 month.</p>
                </div>
                <div className='space-y-1'>
                  <h3 className="text-xl font-semibold text-gray-900">Tech Support</h3>
                  <p className="text-base text-gray-600">Each winner will get a brand new business website, or upgrades to their current one, or even a custom mobile app.</p>
                </div>
                <div className='space-y-1'>
                  <h3 className="text-xl font-semibold text-gray-900">Legal Support</h3>
                  <p className="text-base text-gray-600">Each winner will get support with CAC registration, contract reviews, trademark filing, and other legal essentials.</p>
                </div>
                <div className='space-y-1'>
                  <h3 className="text-xl font-semibold text-gray-900">Business Trip to China</h3>
                  <p className="text-base text-gray-600">One lucky winner will win a trip to explore global markets, connect with suppliers, and unlock international partnerships.</p>
                </div>
              </div>
            </div>
          </div>
          <div className='w-full min-w-[300px] md:min-w-[500px] flex justify-end'>
            <div className='w-full overflow-y-auto'>
              <div
                data-youform-embed
                data-form="hd8h9air"
                data-width="100%"
                data-height="700px"
              />
            </div>
          </div>
        </div>
        <div className='w-full absolute bottom-[50px] hidden lg:flex justify-center items-center'>
          <SvgArrow width="206px" height="140px" />
        </div>
      </section>

      {/* choose */}
      <section id="choose" className="lg:min-h-[700px] flex flex-col items-center justify-start py-[100px] bg-gradient-to-b from-[#FCFCFC] to-[#F1F6F4] gap-8">
        <div className='w-full max-w-[600px] flex flex-col items-center px-6 md:px-1'>
          <h1 className="text-[30px] md:text-[48px] leading-[1em] font-bold text-gray-900 mb-6 text-center">Choose how you want to read Credit Alert</h1>
          <div className='w-fit rounded-full px-4 py-3 bg-red-700 text-white flex flex-col justify-center items-center space-y-1 shadow-lg transition-all duration-700 animate-pulse'>
            <p className="text-xs md:text-base text-center font-semibold">
            Prices are at a 50% discount.
            </p>
            <p className="text-sm md:text-lg text-center font-semibold uppercase tracking-wider">
            Grab yours now!
            </p>
          </div>
        </div>
        <div className='flex flex-col lg:flex-row p-6 gap-10'>
          {/* cards */}
          <div className='w-full max-w-[400px] h-[339px] bg-white hover:bg-[#007A3D] shadow-md rounded-md border border-[#0000000A] px-2 md:px-6 pt-6 flex flex-col justify-between transition-all duration-300 ease-in-out group cursor-pointer' onClick={openSelar}>
            <div className='flex flex-col space-y-2'>
              <p className="text-[18px] md:text-[24px] font-semibold text-black group-hover:text-white transition-colors duration-300">
                Paperback
              </p>
              <p className="text-[14px] md:text-[20px] text-gray-600 group-hover:text-white font-normal transition-colors duration-300">
                For those who love the feel of real pages.
              </p>
            </div>
            <div className='flex items-end justify-between'>
              <div className='flex items-center pb-[10px] space-x-2'>
                <p className="text-[#007A3D] group-hover:text-white font-semibold transition-colors duration-300">
                  <span className="text-[14px] md:text-[24px] group-hover:hidden">Pre-order</span>
                  <span className="text-[14px] md:text-[24px] hidden group-hover:inline leading-tight">Pre-order for ₦15,000 </span>
                </p>
                <span className="relative">
                  <div className="group-hover:opacity-0 group-hover:invisible transition-all duration-300 text-[#007A3D]">
                    <SvgStar width="20px" height="20px" />
                  </div>
                  
                  {/* Second image - hidden by default, visible on hover */}
                  <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <Sparkles width="20px" height="20px"/>
                  </div>
                </span>
              </div>

              {/* Image container with conditional display */}
              <div className="relative">
                {/* First image - visible by default, hidden on hover */}
                <div className="group-hover:opacity-0 group-hover:invisible transition-all duration-300">
                  <GreenBook width="200px" height="170px" />
                </div>
                
                {/* Second image - hidden by default, visible on hover */}
                <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <WhiteBook width="200px" height="170px"/>
                </div>
              </div>
            </div>
          </div>
          <div className='w-full max-w-[400px] h-[339px] bg-white hover:bg-[#C03221] shadow-md rounded-md border border-[#0000000A] px-2 md:px-6 pt-6 flex flex-col justify-between transition-all duration-300 ease-in-out group cursor-pointer' onClick={openEbookSelar}>
            <div className='flex flex-col space-y-2'>
              <p className="text-[18px] md:text-[24px] font-semibold text-black group-hover:text-white transition-colors duration-300">
                E-book
              </p>
              <p className="text-[14px] md:text-[20px] text-gray-600 group-hover:text-white font-normal transition-colors duration-300">
              Read Credit Alert on your phone, tablet, or laptop.
              </p>
            </div>
            <div className='flex items-end justify-between'>
              <div className='flex items-center pb-[10px] space-x-2'>
                <p className="text-[14px] md:text-[24px] text-[#C03221] group-hover:text-white font-semibold transition-colors duration-300">
                  <span className="group-hover:hidden">Buy</span>
                  <span className="hidden group-hover:inline">Read now for ₦5000</span>
                </p>
                <span className="relative">
                  <div className="group-hover:opacity-0 group-hover:invisible transition-all duration-300 text-[#C03221]">
                    <SvgStar width="20px" height="20px" />
                  </div>
                  
                  {/* Second image - hidden by default, visible on hover */}
                  <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <Sparkles width="20px" height="20px"/>
                  </div>
                </span>
              </div>

              {/* Image container with conditional display */}
              <div className="relative">
                {/* First image - visible by default, hidden on hover */}
                <div className="group-hover:opacity-0 group-hover:invisible transition-all duration-300">
                  <PaperRed width="200px" height="170px" />
                </div>
                
                {/* Second image - hidden by default, visible on hover */}
                <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <WhitePaper width="200px" height="170px"/>
                </div>
              </div>
            </div>
          </div>
          <div className='w-full max-w-[400px] h-[339px] bg-white hover:bg-[#C49200] shadow-md rounded-md border border-[#0000000A] px-2 md:px-6 pt-6 flex flex-col relative transition-all duration-300 ease-in-out group cursor-pointer' onClick={openAudioSelar}>
            <div className='flex flex-col space-y-2'>
              <p className="text-[18px] md:text-[24px] font-semibold text-black group-hover:text-white transition-colors duration-300">
                Audio book
              </p>
              <div>
                <p className="text-[14px] md:text-[20px] text-gray-600 group-hover:text-white font-normal transition-colors duration-300">
                  For those who prefer to listen on the go.
                </p>
                <p className="text-[14px] md:text-[20px] text-gray-600 group-hover:text-white font-normal transition-colors duration-300">
                  <span className="font-bold">Audio Voiced by:</span> <br/>
                  <span className='flex items-center space-x-2'><Percent width="14px" height="14px" /> <span>Bambam (Achalugo)</span></span>
                  <span className='flex items-center space-x-2'><Percent width="14px" height="14px" /> <span>Ayo Akinola</span></span>
                </p>
              </div>
            </div>
            <div className='absolute w-full left-0 bottom-0 px-2 md:px-6 flex items-end justify-between'>
              <div className='flex items-center pb-[10px] space-x-2'>
                <p className="text-[14px] md:text-[24px] text-[#C49200] group-hover:text-white font-semibold transition-colors duration-300">
                  <span className="group-hover:hidden">Listen</span>
                  <span className="hidden group-hover:inline">Get audio for ₦7000</span>
                </p>
                <span className="relative">
                  <div className="group-hover:opacity-0 group-hover:invisible transition-all duration-300 text-[#C49200]">
                    <SvgStar width="20px" height="20px" />
                  </div>
                  
                  {/* Second image - hidden by default, visible on hover */}
                  <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                    <Sparkles width="20px" height="20px"/>
                  </div>
                </span>
              </div>

              {/* Image container with conditional display */}
              <div className="relative">
                {/* First image - visible by default, hidden on hover */}
                <div className="transition-all duration-300">
                  <SvgBamBam width="200px" height="170px" />
                </div>
                
                {/* Second image - hidden by default, visible on hover */}
                {/* <div className="absolute inset-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                  <SvgBamBam width="200px" height="170px"/>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* choose */}
      <section id="choose" ref={sectionRef} className="lg:min-h-[700px] flex flex-col items-center justify-start space-y-10 py-[100px] bg-gradient-to-b from-[#FCFCFC] to-[#FAFAFA]">
        <div className='w-full max-w-[600px] flex flex-col items-center'>
          <h2 className="text-[30px] md:text-[48px] leading-[1em] font-bold text-gray-900 mb-6 text-center">60 Seconds Credit Alert Masterclass</h2>
          <p className="text-base md:text-[20px] text-gray-600 font-normal text-center mb-6 max-w-2xl p-4">
          Watch successful Nigerian entrepreneurs give real, practical tips, packed with honest insights on how they were able to sell, build trust, grow their brands, and thrive in the Nigerian market.
          </p>
        </div>
        {/* add embed youtube video that plays when they get to this section and pause when they leave this section */}
          {/* YouTube Video Embed */}
          <div className="w-full max-w-[918px] aspect-video bg-gray-100 border border-[#0000000A] p-[10px] rounded-lg overflow-hidden shadow-lg">
            <div 
              id="youtube-player"
              ref={playerDivRef}
              className="w-full h-full"
            />
          </div>
          
          {/* Optional: Loading state */}
          {!isPlayerReady && (
            <div className="w-full max-w-[560px] aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-gray-500">Loading video...</div>
            </div>
          )}
      </section>

      <section id="about" className="lg:min-h-[700px] flex flex-col items-center justify-start py-[100px] space-y-10 bg-gradient-to-b from-[#FCFCFC] to-[#F1F6F4]">
        <h1 className="text-[30px] md:text-[48px] leading-[1em] font-bold text-gray-900 mb-6 text-center">Meet the Author</h1>
        <div className='container mx-auto pt-[20px] lg:pt-0 px-4 lg:px-8 flex flex-col md:flex-row gap-[100px] justify-center'>
          <div className='w-full max-w-[373px] flex flex-col justify-center'>
            <img 
              src="https://res.cloudinary.com/dizhwjlge/image/upload/v1751137742/ayo_oworpq.png"
              alt="Book Cover"
              className="max-w-full h-auto object-contain"
            />
          </div>
          <div className='w-full md:w-1/2 flex justify-end'>
             <div className='w-full flex flex-col'>
              <p className="text-base md:text-[20px] text-gray-600 font-normal mb-6 max-w-2xl">
              Ayo Akinola is a tech entrepreneur, author, and sales strategist. He’s a co-founder of Piggytech, the parent company of fintech giants like Piggyvest and PocketApp, helping over 7 million Nigerians better manage their finances. 
              </p>
              <p className="text-base md:text-[20px] text-gray-600 font-normal mb-8">
              Ayo served as the Head of Business Development and Sales at Piggyvest and now currently serves as the CEO of PocketApp. He has trained and consulted for over 100 small and medium scale enterprises, helping them achieve their overall sales goals. He holds a degree in computer science from the prestigious Covenant University.
              </p>
              {/* socials */}
              <div className='flex items-center space-x-4'>
                <a className='text-[#007A3D] hover:-translate-y-1 transition-all duration-200' href="https://www.linkedin.com/in/ayoakinola" target="_blank" rel="noopener noreferrer">
                  <MdiLinkedin width="28px" height="28px" />
                </a>
                <a className='text-[#007A3D] hover:-translate-y-1 transition-all duration-200' href="https://www.youtube.com/@ayoakinolayoutube/featured" target="_blank" rel="noopener noreferrer">
                  <SvgYoutube width="28px" height="28px" />
                </a>
                <a className='text-[#007A3D] hover:-translate-y-1 transition-all duration-200' href="https://www.instagram.com/ayakinola" target="_blank" rel="noopener noreferrer">
                  <MdiInstagram width="28px" height="28px" />
                </a>
                {/* <a className='text-[#007A3D] hover:-translate-y-1 transition-all duration-200' href="XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX" target="_blank" rel="noopener noreferrer">
                  <MdiFacebook width="28px" height="28px" />
                </a> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* footer */}
      <section id="choose" className="flex flex-col items-center justify-start py-[100px] bg-white">
        <div className='container mx-auto pt-[20px] lg:pt-0 px-4 lg:px-8 flex flex-col gap-10'>
          {/* logo and menu items */}
          <div className='w-full flex flex-col space-y-6'>
            <button 
              onClick={() => scrollToSection('home')}
              className="flex items-center hover:opacity-80 transition-opacity"
            >
              <SvgLogo width="171px" height="45px" />
            </button>

            <div className="flex flex-col lg:flex-row items-start justify-start lg:items-center gap-3 lg:gap-6">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`py-0 lg:py-2 text-[14px] lg:text-[20px] font-medium transition-colors duration-200 
                      text-gray-900 hover:text-green-600`}
                >
                  {section.name}
                </button>
              ))}
            </div>
          </div>
          {/* year, copy right text and social icons */}
          <div className='w-full flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-0 justify-between border-t border-[#E9EAEB80] pt-[25px]'>
            <p className="text-base md:text-[20px] text-gray-600 font-normal">© {new Date().getFullYear()} All rights reserved.</p>

            <div className='flex items-center space-x-4'>
              <a className='text-[#A4A7AE] hover:-translate-y-1 transition-all duration-200' href="https://www.linkedin.com/in/ayoakinola" target="_blank" rel="noopener noreferrer">
                <MdiLinkedin width="28px" height="28px" />
              </a>
              <a className='text-[#A4A7AE] hover:-translate-y-1 transition-all duration-200' href="https://www.youtube.com/@ayoakinolayoutube/featured" target="_blank" rel="noopener noreferrer">
                <SvgYoutube width="28px" height="28px" />
              </a>
              <a className='text-[#A4A7AE] hover:-translate-y-1 transition-all duration-200' href="https://www.instagram.com/creditalertbook" target="_blank" rel="noopener noreferrer">
                <MdiInstagram width="28px" height="28px" />
              </a>
              <a className='text-[#A4A7AE] hover:-translate-y-1 transition-all duration-200' href="https://www.facebook.com/share/17BtrP4b42/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">
                <MdiFacebook width="28px" height="28px" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default App;